/**
 * PostHog Web Initialization
 *
 * Extracted from analytics.web.ts to keep that file under the 500-line limit.
 * Uses PostHog's official 2025 script snippet instead of the npm package because
 * Metro bundler strips out the networking layer from posthog-js, making it unusable.
 */

import { logger } from "../logger";
import { POSTHOG_KEY, POSTHOG_HOST, shouldInitializePostHog } from "./config";
import { setPostHogCache, updateSessionId } from "./posthog-cache";

declare global {
  interface Window {
    posthog?: any;
  }
}

/**
 * Initialize PostHog for product analytics using official snippet
 *
 * Note: We use PostHog's snippet instead of the npm package because
 * Metro bundler strips out the networking layer from posthog-js, making it
 * unusable. This inline snippet is PostHog's official 2025 method.
 */
export function initPostHog(): void {
  if (!shouldInitializePostHog()) {
    return;
  }

  if (typeof window === 'undefined') {
    logger.log("[Analytics] PostHog skipped (not in browser)");
    return;
  }

  // Idempotency guard - prevent multiple initialization attempts (DRAMA-WEB-23)
  if ((window.posthog as any)?.__initialized) {
    logger.log("[Analytics] PostHog already initialized, skipping duplicate init");
    return;
  }

  // Shared config used by both HMR and fresh-init paths
  const posthogConfig = {
    api_host: POSTHOG_HOST,
    person_profiles: 'always' as const,
    autocapture: false,
    capture_pageview: true,
    capture_pageleave: true,
    session_recording: {
      maskAllInputs: true,
      maskAllText: false,
    },
    loaded: (ph: any) => {
      const distinctId = ph.get_distinct_id?.() || null;
      const sessionId = ph.get_session_id?.() || null;
      setPostHogCache(distinctId, sessionId);
      if (typeof ph.onSessionId === 'function') {
        ph.onSessionId((sid: string) => updateSessionId(sid));
      }
      logger.log('[Analytics] PostHog loaded - session cached', { hasDistinctId: !!distinctId, hasSessionId: !!sessionId });
    },
  };

  // CRITICAL FIX (2026-01-26): Prevent snippet from running on module reload (HMR)
  if (window.posthog) {
    logger.log("[Analytics] PostHog snippet already loaded, skipping duplicate snippet execution");
    window.posthog.init(POSTHOG_KEY, posthogConfig);

    (window.posthog as any).__initialized = true;

    logger.log("[Analytics] PostHog re-initialized with config (HMR recovery)");
    return;
  }

  // Official PostHog snippet (2025 version) with React 19 compatibility fix
  // @ts-ignore - Official PostHog loader snippet (unmodified)
  !function(t,e){var o,n,p,r;e.__SV||(window.posthog && (window.posthog as any).__loaded)||(window.posthog=e,e._i=[],e.init=function(i:any,s:any,a:any){function g(t:any,e:any){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",(p as any).crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode!.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],
  // React 19 Compatibility: Wrap toString assignments to handle frozen objects
  (function(){try{u.toString=function(t:any){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e}}catch(e){}})(),
  (function(){try{u.people.toString=function(){return u.toString(1)+".people (stub)"}}catch(e){}})(),
  o="capture identify alias register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey displaySurvey canRenderSurvey canRenderSurveyAsync setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing get_explicit_consent_status is_capturing clear_opt_in_out_capturing debug getPageViewId captureTraceFeedback captureTraceMetric".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);

  // Initialize with official config
  window.posthog!.init(POSTHOG_KEY, posthogConfig);

  // Mark initialization as complete for feature flags hook
  if (window.posthog) {
    (window.posthog as any).__initialized = true;
  }

  // Redirect Domain Attribution Tracking (Cloudflare Worker Integration)
  // @see packages/cloudflare-redirect-infra/worker.js
  if (typeof document !== 'undefined' && window.posthog) {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      let redirectDomain = urlParams.get('rd');

      if (!redirectDomain) {
        redirectDomain = document.cookie
          .split('; ')
          .find(row => row.startsWith('redirect_domain='))
          ?.split('=')[1] || null;
      }

      if (redirectDomain) {
        window.posthog.register_once({ redirect_domain: redirectDomain });
        window.posthog.setPersonProperties({
          initial_redirect_domain: redirectDomain
        });
        logger.log(`[Analytics] Redirect domain tracked: ${redirectDomain}`);

        if (urlParams.has('rd')) {
          urlParams.delete('rd');
          const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}${window.location.hash}`;
          window.history.replaceState({}, '', newUrl);
        }

        document.cookie = 'redirect_domain=; Max-Age=0; Path=/';
      }
    } catch (error) {
      logger.debug('[Analytics] Failed to read redirect_domain:', error);
    }
  }

  logger.log("[Analytics] PostHog initialized with fixed queue behavior (DRAMA-WEB-23: method queue separation, idempotency guard)");
}
